/* WeQuote Dev Board — Needs-Review intake + resolve logic (PRD §5.9).
 * Pure ports of reviewIntakeLine, similarCards/similarCount, reviewReqLabel,
 * mergeInto, reviewAddToDev, addFeature (manual card) and the demo support-chat
 * intake (kept as a demo per the build agreement; the real support-chat
 * pathway is a post-parity integration). */
import type { Feature } from './types'
import { calcEffort } from './constants'
import { todayISO, fmtDate, daysAgo, daysAgoTxt } from './dates'
import { reqCount } from './logic'
import { firstReqDate, migrate } from './migrate'
import { insertByScore } from './suggest'
import type { MutResult } from './mutations'

export function reviewIntakeLine(feat: Partial<Feature>): string {
  const s = feat.source || ({} as any)
  let date = s.ts ? ('' + s.ts).slice(0, 10) : ''
  if (!date) {
    ;(feat.requests || []).forEach((r) => { if (r.date && (!date || r.date < date)) date = r.date })
  }
  const company = s.type === 'chat' && s.company
    ? s.company
    : feat.requests && feat.requests[0] && feat.requests[0].company
      ? feat.requests[0].company
      : feat.cust || 'Unknown'
  const da = daysAgo(date)
  const when = date ? fmtDate(date) + (da !== null ? ' (' + daysAgoTxt(da) + ')' : '') : 'date unknown'
  return 'Requested on ' + when + ' by ' + company
}

export interface Scored { feat: Feature; sim: number }
export function similarScored(feat: Partial<Feature>, features: Feature[]): Scored[] {
  const words = (feat.title || '').toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3)
  const cands = features.filter((f) => f.id !== feat.id && f.status !== 'needsreview' && f.status !== 'archived')
  const scored = cands.map((c) => {
    const t = (c.title || '').toLowerCase()
    let score = 0
    words.forEach((w) => { if (t.indexOf(w) > -1) score++ })
    return { feat: c, sim: score }
  })
  scored.sort((a, b) => b.sim - a.sim || a.feat.po - b.feat.po)
  return scored
}
export function similarCount(feat: Partial<Feature>, features: Feature[]): number {
  return similarScored(feat, features).filter((c) => c.sim > 0).length
}

/** Compact "requested by …" label (earliest company + others) — Overview + review rows. */
export function reviewReqLabel(feat: Feature): string {
  const reqs = (feat.requests || []).filter((r) => r.company)
  if (reqs.length) {
    const sorted = reqs.slice().sort((a, b) => {
      const da = a.date || '9999'
      const db = b.date || '9999'
      return da < db ? -1 : da > db ? 1 : 0
    })
    const first = sorted[0]
    const set: Record<string, 1> = {}
    reqs.forEach((r) => { set[(r.company || '').trim().toLowerCase()] = 1 })
    const others = Math.max(0, Object.keys(set).length - 1)
    const d = first.date || firstReqDate(feat) || ''
    return 'Requested by ' + first.company + (others > 0 ? ' +' + others + ' other' + (others === 1 ? '' : 's') : '') + (d ? ' · ' + fmtDate(d) : '')
  }
  const s = feat.source
  if (s && s.type === 'chat' && s.company) return 'Requested by ' + s.company + (feat.dateAdded ? ' · ' + fmtDate(feat.dateAdded) : '')
  if (s && s.type === 'manual') return 'Added by ' + (s.by || 'Unknown') + (feat.dateAdded ? ' · ' + fmtDate(feat.dateAdded) : '')
  return 'Requester not recorded'
}

/* ── resolve actions ── */
export function reviewAddToDev(fs: Feature[], id: number): MutResult {
  let title = ''
  const next = fs.map((f) => {
    if (f.id !== id) return f
    title = f.title
    return { ...f, status: 'later', onhold: false } // v2 §1: review cards land in Later
  })
  const scored = insertByScore(next, id) // v2 §1: slot at its scored position within Later
  return { features: scored, toast: '“' + title + '” added to the dev list (Later).', changedIds: [id] }
}

/** Merge a review card into an existing card: demand + context move across,
 *  the source is deleted. Returns removedId so the caller can delete it. */
export function mergeInto(fs: Feature[], sourceId: number, targetId: number): MutResult & { removedId?: number } {
  const src = fs.find((f) => f.id === sourceId)
  const tgt = fs.find((f) => f.id === targetId)
  if (!src || !tgt) return { features: fs }
  let entries = []
  if (Array.isArray(src.requests) && src.requests.length) entries = src.requests.slice()
  else if (src.source && src.source.type === 'chat') entries = [{ company: src.source.company || '', person: src.source.person || '', date: src.source.ts ? src.source.ts.slice(0, 10) : todayISO() }]
  else entries = [{ company: src.cust || '', person: (src.source && src.source.by) || '', date: todayISO() }]
  const next = fs
    .filter((f) => f.id !== sourceId)
    .map((f) => {
      if (f.id !== targetId) return f
      const g = { ...f, requests: [...(f.requests || []), ...entries] }
      if (src.notes && ('' + src.notes).trim()) {
        g.comments = [...(g.comments || []), { author: 'Merged from review', text: src.notes, ts: new Date().toISOString() }]
      }
      return g
    })
  const merged = next.find((f) => f.id === targetId)!
  return {
    features: next,
    toast: 'Merged into “' + tgt.title + '” · request count now ' + reqCount(merged) + '.',
    changedIds: [targetId],
    removedId: sourceId,
  }
}

/* ── intake builders ── */
export interface AddFeatureFields {
  title: string; cust: string; pri: string; time: string; deadline: string
  designDeadline: string; start: string; notes: string; requiresDesign: boolean
  reqCompany: string; reqPerson: string; module: string; isBug: boolean
  reqCrmId?: number | string; reqCrmKind?: 'paid' | 'deal' // set when matched to a CRM record
}
export function buildManualCard(po: number, f: AddFeatureFields, by: string): Feature {
  const req0: any = { company: f.reqCompany.trim(), person: f.reqPerson.trim() || '', date: todayISO() }
  if (f.reqCrmId != null) { req0.crmId = f.reqCrmId; req0.crmKind = f.reqCrmKind }
  const reqs = f.reqCompany.trim() ? [req0] : []
  return migrate({
    po,
    title: f.title.trim(),
    init: '',
    module: f.module,
    pri: f.pri,
    eff: calcEffort(f.time) || 'medium',
    status: 'needsreview',
    dev: '',
    cust: f.cust,
    time: f.time,
    deadline: f.deadline,
    designDeadline: f.designDeadline,
    start: f.start,
    progress: 0, pnotes: '', onhold: false, prevStatus: '', stash: null,
    links: [],
    notes: f.notes.trim(),
    requiresDesign: f.requiresDesign,
    isBug: !!f.isBug,
    requests: reqs,
    dateAdded: todayISO(),
    source: { type: 'manual', by, ts: new Date().toISOString() },
    archiveReason: '',
  })
}

export const CHAT_DEMOS = [
  { title: 'Dark mode for the customer portal', company: 'Lumen Installs', person: 'Tom Beckett', pri: 'low', notes: 'Requested in support chat — eye strain for late-night scheduling. Tagged as a feature request by the agent.' },
  { title: 'Export purchase orders to CSV', company: 'Avenue Systems', person: 'Dan Okafor', pri: 'medium', notes: 'Chat escalation: wants to export the PO list for their accountant each month.' },
  { title: 'Customisable quote PDF cover page', company: 'Triangle AV', person: 'Mia Cole', pri: 'medium', notes: 'Chat: asked for a branded cover page on exported quote PDFs. Would help them win more tenders.' },
  { title: 'Reminders for overdue customer approvals', company: 'Blue Vision', person: 'Ella Ross', pri: 'low', notes: 'Chat: customers sit on proposals; wants an automatic nudge after X days.' },
]
export function buildChatCard(po: number, idx: number): Feature {
  const d = CHAT_DEMOS[idx % CHAT_DEMOS.length]
  return migrate({
    po,
    title: d.title,
    init: 'From support',
    pri: d.pri,
    eff: 'medium',
    status: 'needsreview',
    dev: '', cust: d.company, time: '', deadline: '', designDeadline: '', start: '',
    progress: 0, pnotes: '', onhold: false, prevStatus: '', stash: null, links: [], comments: [],
    notes: d.notes,
    requiresDesign: true,
    archiveReason: '',
    dateAdded: todayISO(),
    source: { type: 'chat', company: d.company, person: d.person, ts: new Date().toISOString() },
    requests: [{ company: d.company, person: d.person, date: todayISO() }],
  })
}
